import type { ReactNode } from 'react';

import { Body, cn, useContentMask } from '@growtherapy/sprout-ui';

import { LoadingSpinner } from '../../components/LoadingSpinner';
import type { AdmittedClientStatusResult } from './useAdmittedClientStatus';

const STATUS_COPY = {
  sending_invite: (_visitorName: ReactNode) => <>Sending Invitation…</>,
  waiting_for_click: (visitorName: ReactNode) => <>Waiting for {visitorName} to click Join</>,
  preparing: (visitorName: ReactNode) => <>{visitorName} is preparing…</>,
  joining: (visitorName: ReactNode) => <>{visitorName} is connecting…</>,
  not_on_page: (visitorName: ReactNode) => <>{visitorName} may have left the page</>,
};

function LegacyJoiningClientLoader() {
  return (
    <div className='flex gap-2 items-center text-small'>
      <LoadingSpinner aria-hidden />
      <Body.Small>Waiting for client to join</Body.Small>
    </div>
  );
}

function GranularJoiningClientLoader({
  admittedClientStatus,
  onDarkSurface,
  visitorName,
}: {
  admittedClientStatus: AdmittedClientStatusResult;
  onDarkSurface?: boolean;
  visitorName: string;
}) {
  const { maskClassName, maskProps } = useContentMask({ mask: 'exclude' });
  const { status, showSpinner, shouldPulse } = admittedClientStatus;

  if (status === 'connected') {
    return null;
  }

  const spinnerClassName = cn('fa-spin text-neutral-900', {
    'rebrand:text-text-inverse': onDarkSurface,
    'text-text-muted rebrand:text-text-muted': shouldPulse,
  });

  const maskedName = (
    <span className={maskClassName} {...maskProps}>
      {visitorName}
    </span>
  );

  return (
    <div
      className={cn('flex gap-2 items-center text-small', {
        'rebrand:text-text-inverse': onDarkSurface,
        'animate-pulse text-text-muted rebrand:text-text-muted': shouldPulse,
      })}
    >
      {showSpinner ? <LoadingSpinner aria-hidden className={spinnerClassName} /> : null}
      <Body.Small
        aria-live='polite'
        className={cn({
          'rebrand:text-text-inverse': onDarkSurface,
          'rebrand:text-text-muted': shouldPulse,
        })}
        data-testid='joining-client-loader.status'
      >
        {STATUS_COPY[status](maskedName)}
      </Body.Small>
    </div>
  );
}

export function JoiningClientLoader(): JSX.Element;
/**
 * `onDarkSurface` opts into inverse (light) text for dark backgrounds like the
 * clients drawer. Surfaces with a light background, e.g. the client queue
 * toast, use the default text color.
 */
export function JoiningClientLoader(props: {
  admittedClientStatus: AdmittedClientStatusResult;
  onDarkSurface?: boolean;
  visitorName: string;
}): JSX.Element | null;
export function JoiningClientLoader({
  admittedClientStatus,
  onDarkSurface,
  visitorName,
}: {
  admittedClientStatus?: AdmittedClientStatusResult;
  onDarkSurface?: boolean;
  visitorName?: string;
} = {}) {
  if (!admittedClientStatus || !visitorName) {
    return <LegacyJoiningClientLoader />;
  }

  return (
    <GranularJoiningClientLoader
      admittedClientStatus={admittedClientStatus}
      onDarkSurface={onDarkSurface}
      visitorName={visitorName}
    />
  );
}
